export function convertMeasurement(value, unit) {
  const text = String(value).trim();
  if (unit !== 'cm' || !/^\d+(?:\.\d+)?$/.test(text)) return text;
  return String(Math.round(Number(text) * 2.54 * 10) / 10);
}

export function unitLabel(value, unit) {
  return String(value).replace(/,\s*in\.?$/i, unit === 'cm' ? ', cm' : ', in');
}

export function isMeasurementCell(chart, rowIndex, columnIndex) {
  if (rowIndex === 0 || columnIndex === 0) return false;
  const label = chart.orientation === 'rows' ? chart.rows[0][columnIndex] : chart.rows[rowIndex][0];
  return /\b(width|length|bust|chest|shoulder|sleeve|waist|hip|inseam|rise|thigh|opening|tolerance|circumference|neck)\b/i.test(label)
    && /^\d+(?:\.\d+)?$/.test(chart.rows[rowIndex][columnIndex]);
}

export function normalizeSizeRows(rows) {
  if (!Array.isArray(rows) || rows.length < 2 || rows[0].length < 2) return null;
  const width = rows[0].length;
  const clean = rows.map((row) => row.map((cell) => String(cell).replace(/\s+/g, ' ').trim()));
  if (clean.some((row) => row.length !== width)) return null;
  const firstLabel = clean[0][0].toLowerCase();
  const sizesInRows = /^(size|sizes)$/.test(firstLabel);
  const sizesInColumns = firstLabel === '' || firstLabel === 'measurement';
  if (!sizesInRows && !sizesInColumns) return null;
  const chart = { rows: clean, orientation: sizesInRows ? 'rows' : 'columns' };
  if (!clean.some((row, rowIndex) => row.some((cell, columnIndex) => isMeasurementCell(chart, rowIndex, columnIndex)))) return null;
  return chart;
}

if (globalThis.customElements && !customElements.get('mvurra-size-guide')) {
  let instanceCount = 0;

  class MvurraSizeGuide extends HTMLElement {
    static observedAttributes = ['hidden', 'data-product-id'];

    attributeChangedCallback(name, previous, value) {
      if (previous === value || !this.controller || !this.isConnected || this.refreshPending) return;
      if (name === 'hidden' && value === null) return;
      this.refreshPending = true;
      queueMicrotask(() => {
        this.refreshPending = false;
        if (!this.isConnected) return;
        this.controller?.abort();
        if (this.dialog?.open) this.dialog.close();
        this.controller = null;
        this.built = false;
        this.connectedCallback();
        this.releaseScroll();
      });
    }

    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('[data-size-guide-open]');
      const source = this.querySelector('[data-size-guide-source]')?.content;
      const sourceTable = source?.querySelector('table');
      const sourceRows = sourceTable ? [...sourceTable.rows].map((row) => [...row.cells].map((cell) => cell.textContent)) : [];
      this.chart = normalizeSizeRows(sourceRows);
      if (!this.chart || !this.dialog || !this.trigger || typeof this.dialog.showModal !== 'function') return;

      const id = `MvurraSizeGuide-${this.dataset.productId}-${++instanceCount}`;
      this.dialog.id = id;
      this.trigger.setAttribute('aria-controls', id);
      this.dialog.querySelector('h2').id = `${id}-title`;
      this.dialog.querySelector('.mv-size-guide__intro').id = `${id}-intro`;
      this.dialog.setAttribute('aria-labelledby', `${id}-title`);
      this.dialog.setAttribute('aria-describedby', `${id}-intro`);
      this.fitNotes = [...source.querySelectorAll('p, li')]
        .filter((node) => !node.closest('table') && !node.closest('.mvurra-product-policy') && !node.closest('.mvurra-product-shipping'))
        .map((node) => node.textContent.replace(/\s+/g, ' ').trim())
        .filter((text) => /\b(fit|stretch|stretchy|oversized|measurements|tolerance)\b/i.test(text) && !/\b(shipping|refund|returns|production|product measurements \(measurements by inches\))\b/i.test(text));
      this.fitNotes = [...new Set(this.fitNotes)];

      this.trigger.addEventListener('click', () => this.open(), { signal });
      this.querySelector('[data-size-guide-close]').addEventListener('click', () => this.close(), { signal });
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, { signal });
      this.dialog.addEventListener('close', () => this.restoreFocus(), { signal });
      this.dialog.addEventListener('click', (event) => {
        event.stopPropagation();
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
      }, { signal });
      this.dialog.addEventListener('keydown', (event) => this.keepFocus(event), { signal });
      this.querySelectorAll('[data-size-guide-unit]').forEach((button) => button.addEventListener('click', () => this.setUnit(button.dataset.sizeGuideUnit), { signal }));
      this.hidden = false;
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      this.controller = null;
      this.releaseScroll();
    }

    buildTable() {
      const table = document.createElement('table');
      const head = table.createTHead();
      const body = table.createTBody();
      this.chart.rows.forEach((row, rowIndex) => {
        const tr = document.createElement('tr');
        row.forEach((value, columnIndex) => {
          const cell = document.createElement(rowIndex === 0 || columnIndex === 0 ? 'th' : 'td');
          if (rowIndex === 0) cell.scope = 'col';
          else if (columnIndex === 0) cell.scope = 'row';
          const isMeasurement = isMeasurementCell(this.chart, rowIndex, columnIndex);
          const label = rowIndex === 0 && columnIndex === 0 && !value ? this.dataset.measurementLabel : value;
          cell.textContent = label;
          cell.dataset.original = label;
          if (isMeasurement) cell.dataset.measurement = value;
          const isUnitLabel = this.chart.orientation === 'columns' && columnIndex === 0 && rowIndex > 0;
          if (isUnitLabel) cell.dataset.unitLabel = value;
          tr.append(cell);
        });
        (rowIndex === 0 ? head : body).append(tr);
      });
      this.querySelector('[data-size-guide-table]').replaceChildren(table);
      const notes = this.querySelector('[data-size-guide-notes]');
      notes.replaceChildren(...this.fitNotes.map((text) => {
        const paragraph = document.createElement('p');
        paragraph.textContent = text;
        return paragraph;
      }));
      this.built = true;
      this.setUnit('in');
    }

    setUnit(unit) {
      if (!['in', 'cm'].includes(unit)) return;
      this.querySelectorAll('[data-measurement]').forEach((cell) => { cell.textContent = convertMeasurement(cell.dataset.measurement, unit); });
      this.querySelectorAll('[data-unit-label]').forEach((cell) => { cell.textContent = unitLabel(cell.dataset.unitLabel, unit); });
      this.querySelectorAll('[data-size-guide-unit]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.sizeGuideUnit === unit)));
      this.querySelector('[data-size-guide-unit-label]').textContent = unit === 'cm' ? this.dataset.unitCm : this.dataset.unitIn;
      this.querySelector('[data-size-guide-conversion]').hidden = unit !== 'cm';
    }

    open() {
      if (this.dialog.open) return;
      if (!this.built) this.buildTable();
      this.returnFocus = document.activeElement;
      this.dialog.showModal();
      document.documentElement.classList.add('mv-size-guide-open');
      this.querySelector('[data-size-guide-close]').focus();
    }

    close() { if (this.dialog.open) this.dialog.close(); }

    releaseScroll() {
      if (!document.querySelector('mvurra-size-guide dialog[open]')) document.documentElement.classList.remove('mv-size-guide-open');
    }

    restoreFocus() {
      this.releaseScroll();
      const target = this.returnFocus?.isConnected ? this.returnFocus : this.trigger;
      if (target?.isConnected) target.focus({ preventScroll: true });
    }

    keepFocus(event) {
      if (event.key === 'Escape') { event.stopPropagation(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.disabled && element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  }

  customElements.define('mvurra-size-guide', MvurraSizeGuide);
}
